import React, { useEffect, useState } from "react";
import {
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import "./App.css";

const Contact = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  // Loading state: true while the POST request is in flight.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snackbar feedback: replaces native alert() with an elegant toast.
  // severity = "success" | "error" → controls icon + color of the Alert.
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const closeSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setMessage("");
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const lang = searchParams.get("lang");
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, [location, i18n]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = {
      firstName,
      lastName,
      email,
      phone,
      message,
    };

    try {
      // Η Azure Function τρέχει στο ίδιο domain με το site (Static Web App).
      // Τοπικά, το "proxy" του package.json στέλνει το /api στο func (port 7071).
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: t("contactSuccess"),
          severity: "success",
        });
        resetForm();
      } else {
        setSnackbar({
          open: true,
          message: t("contactError"),
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbar({
        open: true,
        message: t("contactError"),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Grid container spacing={4}>
        {/* Contact Info & Map */}
        <Grid item xs={12} md={6}>
          {/* .home-paper class handles padding/radius/shadow */}
          <Paper elevation={3} className="home-paper">
            {/* h4 typography from MUI theme */}
            <Typography variant="h4" align="center" sx={{ mb: 1 }}>
              {t("Contact us")}
            </Typography>

            <Typography variant="body1" gutterBottom align="left">
              <span className="label-style">{t("Address: ")}</span>
              <a
                href="https://www.google.com/maps/place/Apokoronou+84,+Chania+731+35,+Greece"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#576a89",
                  textDecoration: "underline",
                }}
              >
                {t("Apokoronou 84, 73134 Chania")}
              </a>
            </Typography>

            <Typography variant="body1" align="left" gutterBottom>
              <span className="label-style">{t("Phone Number")}</span>{" "}
              <a
                href="tel:+302821088004"
                style={{ color: "#576a89", textDecoration: "underline" }}
              >
                +30 28210 88004
              </a>
            </Typography>

            <Typography
              variant="body1"
              gutterBottom
              style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
            >
              <span className="label-style">Email: </span>
              <a href="mailto:maniosike@gmail.com" style={{ color: "#576a89" }}>
                maniosike@gmail.com
              </a>
              {" - "}
              <a href="mailto:maniosdi@gmail.com" style={{ color: "#576a89" }}>
                maniosdi@gmail.com
              </a>
            </Typography>

            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3162.4395564763527!2d24.0196321!3d35.5099977!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x149c7d9507d5e9d9%3A0x74a2ed8e6e6556a7!2sApokoronou%2084%2C%20Chania%20731%2035%2C%20Greece!5e0!3m2!1sen!2sus!4v1691484421925!5m2!1sen!2sus"
              width="100%"
              height="350"
              style={{ border: 0, marginTop: 24, borderRadius: 12 }}
              allowFullScreen
              loading="lazy"
              title="map"
            />
          </Paper>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} className="home-paper">
            <Typography variant="h4" align="center" sx={{ mb: 1 }}>
              {t("Contact Form")}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("First Name")}
                    fullWidth
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("Last Name")}
                    fullWidth
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("Email")}
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("Phone")}
                    type="tel"
                    fullWidth
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("Message")}
                    multiline
                    rows={4}
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  {/* Theme primary color → slate bg with white text → stands out */}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                    sx={{
                      width: "100%",
                      maxWidth: 190,
                      py: 1,
                      px: 2,
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      borderRadius: 1,
                    }}
                  >
                    {isSubmitting ? t("Sending") : t("Submit")}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>

      {/* Elegant toast notification — replaces native alert().
          autoHideDuration: success closes faster (5s), error stays longer (8s)
          so the user has time to copy the message and try again. */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "success" ? 5000 : 8000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", maxWidth: 480, fontSize: "0.95rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Contact;
